import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon, Dropdown, Container, Button, Label,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment-timezone';
import util from 'utils/utils';
import useTheme from 'theme/useTheme';
import withTheme from 'theme/withTheme';

import { Banner } from 'components/lib/UI';
import { UserChip, ProfileChip, AnonymousChip } from 'components/lib/Chips';
import Tag, { TagContainer } from 'components/lib/Tag';

export const StyledCoverImage = styled.div`
  border-radius: 3px;
  width: ${({ theme }) => (theme.sizes.isMobile ? '100px' : '200px')};
  height: ${({ theme }) => (theme.sizes.isMobile ? '100px' : '200px')};
  border: 3px solid white;
  margin-right: 15px;
  box-shadow: 0px 10px 10px rgba(0,0,0,0.3);
  background-size:cover;
  background-position:center center;
  background-image: url(${(props) => props.image});
`;

export const StyledContainer = styled(Container)`
  ${({ theme, fluid }) => (theme.sizes.isComputer ? `
    padding: 60px 0px 30px;
    ${fluid && `
      padding: 1rem 3rem;
    `}
  ` : `
    padding: 10px 0;
  `)}
`;

const MiniButton = withTheme(({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  theme, ...props
}) => (
  <Button {...props} size={theme.sizes.isMobile ? 'mini' : null} />
));

export const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? 'column' : 'row')};
  justify-content: space-between;
  align-items: ${({ theme }) => (theme.sizes.isMobile ? 'flex-start' : 'flex-end')};
  ${({ isPreview }) => isPreview && `
    padding: 0 2rem;
  `}
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? 'column' : 'row')};
  align-items: ${({ theme }) => (theme.sizes.isMobile ? 'center' : 'flex-start')};
  ${({ theme }) => (theme.sizes.isMobile && `
    margin-top: 5px;
  `)}
  width: 100%;
  h1, h5, span:not(.dropdown .text) {
    margin: 0;
    color: white;
  }
  .titles {
    margin-bottom: 1rem;
    .meta {
      display: block;
      font-size: 1.1em;
      margin-bottom: 2.5px;
    }
  }
  .owner {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: ${({ user }) => user && '1rem'};
    color: white;
    span {
      display: block;
      margin-right: 5px;
    }
  }
  .info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .sector {
    text-transform: uppercase;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  .ui.button {
    &:not(:last-of-type) {
      margin-right: 0.5rem;
    }
  }
  ${({ theme }) => theme.sizes.isMobile && `
    margin-bottom: 1rem;
  `}
`;

function ChallengeHeader({
  challenge = { name: 'Challenge', ownerOrganisation: { name: 'Simply Do Ideas' }, postedBy: { profile: { fullName: 'User' } } }, myIdeas, newIdea, user, t, isPreview, isIdeasPage, redirectToLogin,
}) {
  const canMakeIdea = util.canCreateIdea(user, challenge);
  const { name } = challenge;
  const theme = useTheme();

  const myUnsubmittedIdeas = useMemo(() => myIdeas?.filter((idea) => !idea.isSubmitted), [myIdeas]);

  return (
    <Banner marginless image={challenge.bannerImageUrl}>
      <StyledContainer fluid={isIdeasPage}>
        <div className='overlay' />
        <div className='overlay-body'>
          <TagContainer>
            {challenge.fullTags && challenge.fullTags.map((tag) => <Tag tag={tag} />)}
          </TagContainer>
          <HeaderContainer isPreview={isPreview}>
            <HeaderInfo user={user}>
              {challenge.coverImageUrl && <StyledCoverImage image={util.getThumbnail(challenge, 'coverImage', '500x500')} />}
              <div className='info'>
                <div className='titles'>
                  <h1 style={{ display: theme.sizes.isMobile && 'contents' }} className='challenge-tour-step-1'>{name}</h1>
                  {challenge.tagline
                    ? (
                      <span className='meta'>{challenge.tagline}</span>
                    ) : null
                  }
                  {challenge.ownerOrganisation
                    ? (
                      <h5> {t('challenge.managedBy')} {challenge.ownerOrganisation.name}</h5>
                    ) : null
                  }
                </div>
                <div>
                  <div className='owner'>
                    <span className='posted-by' style={{ marginRight: 5 }}>{t('challenge.postedBy')}</span>
                    {challenge.anonymous && <AnonymousChip />}
                    {!challenge.anonymous && challenge.postedBy && (
                      challenge.profile ? (
                        <ProfileChip profile={challenge.postedBy} inverted />
                      ) : (
                        <UserChip user={challenge.postedBy} withShadow inverted />
                      )
                    )}
                  </div>
                  {!user
                    && <div style={{ marginBottom: '2rem' }}>
                      {!challenge.stage
                        && <Label color='yellow' style={{ marginRight: 10 }}>{t('challenge.stages.draft.title')}</Label>
                      }
                      {challenge.stage === 'published'
                        && <Label color='green' style={{ marginRight: 10 }}>{t('challenge.stages.published.title')}</Label>
                      }
                      {challenge.stage === 'closed'
                        && <Label color='orange' style={{ marginRight: 10 }}>{t('challenge.stages.closed.title')}</Label>
                      }
                    </div>
                  }
                  {challenge.deadline
                    ? <>
                      <span>
                        <Icon name='calendar' />
                        {t('challenge.deadline')}: {moment(challenge.deadline).format('HH:mm on DD/MM/YYYY')}
                      </span>
                    </> : null
                  }
                </div>
                <HeaderActions>
                  {!challenge.bannerImage && util.canManageChallenge(user, challenge)
                    && <MiniButton style={{ marginBottom: 3 }} circular icon='image' content={'Add a banner image'} as={Link} to={`/challenges/${challenge._id}/settings`} />
                  }
                  {challenge.stage === 'published' && (!user || !canMakeIdea)
                    && <MiniButton circular
                      primary
                      icon='plus'
                      content={t('challenge.ideas.createYourIdea')}
                      onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                    />
                  }
                  {((user && canMakeIdea) && (challenge.stage === 'published' || util.canManageChallenge(user, challenge)))
                    ? (
                      <MiniButton circular icon='plus' content={myIdeas.length > 0 ? t('challenge.startNew') : 'Create your idea'} primary={myIdeas.length === 0} inverted={myIdeas.length > 0} onClick={newIdea} />
                    ) : null
                  }
                  {user && myUnsubmittedIdeas.length > 0
                    && <React.Fragment>
                      {myUnsubmittedIdeas.length === 1
                        && <MiniButton style={{ marginTop: 3 }} circular icon='lightbulb' content={t('challenge.continueIdea')} primary as={Link} to={`/ideas/${myUnsubmittedIdeas[0]._id}`} />
                      }
                      {myUnsubmittedIdeas.length > 1
                        && <Dropdown style={{ marginTop: 3 }} icon={null} direction='left' pointing='top right'
                          trigger={<MiniButton circular icon='lightbulb' content={t('challenge.continueIdea')} primary />}
                        >
                          <Dropdown.Menu>
                            {myUnsubmittedIdeas.map((i, index) => <Dropdown.Item key={index} as={Link} to={`/ideas/${i._id}`}>{i.name}</Dropdown.Item>)}
                          </Dropdown.Menu>
                        </Dropdown>
                      }
                    </React.Fragment>
                  }
                </HeaderActions>
              </div>
            </HeaderInfo>
          </HeaderContainer>
        </div>
      </StyledContainer>
    </Banner >
  );
}

const mapStateToProps = (state, props) => ({ user: props.isPreview ? null : state.user });

export default withTranslation()(connect(mapStateToProps)(ChallengeHeader));
